// ─────────────────────────────────────────────────────────────
//  Экран «Тарифы»: что можно купить и сколько это стоит.
//
//  Цены НЕ считаются здесь. Бэк (GET /api/v1/tariffs) отдаёт их уже
//  посчитанными под регион пользователя: скидка тарифа и региональная наценка
//  применены, price — итог к оплате, full_price — зачёркнутая цена без скидки.
//  Экран только раскладывает готовые числа по карточкам, поэтому изменение
//  цены или процента в админке видно здесь сразу и без правок фронта.
//
//  Состав («что входит») тянется отдельным запросом по тапу: у подписки Таро
//  это все расклады каталога — держать их в списке тарифов было бы тяжело.
//
//  Оплата. По кнопке бэк выставляет счёт и отдаёт ссылку на форму Продамуса; она
//  открывается во ВНЕШНЕМ браузере (openExternalLink), потому что во встроенном
//  вебвью Telegram ломается 3-D Secure и возврат из приложения банка. Мини-апп при
//  этом не закрывается и остаётся ждать: подтверждает оплату не редирект, а вебхук
//  кассы, поэтому результат мы узнаём опросом GET /payments/{id} — он идёт, пока
//  вкладка мини-аппа снова не станет видимой и платёж не перейдёт в is_paid.
//
//  Исключение — пробный тариф (is_trial): он стоит 0 ₽, счёта в кассе на такую сумму
//  не бывает, поэтому у него своя кнопка «Забрать бесплатно». Она зовёт
//  POST /tariffs/trial/activate, доступ приходит прямо в ответе — ни браузера, ни
//  опроса. Забрать его можно один раз, поэтому кнопка гаснет по trial.activated,
//  который приходит сверху вместе с состоянием подарка.
//
//  Промокоды. Код активируют ссылкой на бота (?start=<код>) или полем на этом экране,
//  а применяет его бэк: цены в GET /tariffs уже посчитаны с лучшим из кодов человека
//  (tariff.promo), и счёт выставляется на ту же сумму. Экран только показывает, что
//  код сработал: старая цена зачёркнута, рядом новая и плашка с кодом. Скоро
//  кончающиеся скидки и коды (discount_ending_soon / promo.ending_soon) получают
//  строку «успей» — по сроку или по оставшимся использованиям.
// ─────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, Check, ChevronDown, Clock, ExternalLink, Gift, Ticket, X } from 'lucide-react'
import {
  activatePromoCode, activateTrialTariff, createPayment, fetchMyPromoCodes, fetchTariff, fetchTariffs, getPaymentStatus,
} from '../api.js'
import { hapticNotification, openExternalLink } from '../tg.js'
import { Glass, money, Orb, Pill, SparkLine } from './ui.jsx'

const plural = (n, one, few, many) => {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
  return many
}
const nReadings = n => `${n} ${plural(n, 'разбор', 'разбора', 'разборов')}`
const nQuestions = n => `${n} ${plural(n, 'вопрос', 'вопроса', 'вопросов')}`
const nDays = n => `${n} ${plural(n, 'день', 'дня', 'дней')}`
const nSpreads = n => `${n} ${plural(n, 'расклад', 'расклада', 'раскладов')}`
const nUses = n => `${n} ${plural(n, 'использование', 'использования', 'использований')}`
const nPurchases = n => `${n} ${plural(n, 'покупка', 'покупки', 'покупок')}`

// Бэк хранит время в UTC без таймзоны — дописываем Z, иначе браузер прочтёт его как местное.
const parseUtc = iso => (iso ? new Date(/Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`) : null)

/** «через 5 ч» / «через 2 дня» — сколько осталось до конца скидки или промокода. */
function timeLeft(iso) {
  const end = parseUtc(iso)
  if (!end) return ''
  const hours = Math.max(0, Math.ceil((end - Date.now()) / 3600000))
  if (hours < 1) return 'меньше чем через час'
  if (hours < 24) return `через ${hours} ${plural(hours, 'час', 'часа', 'часов')}`
  const days = Math.ceil(hours / 24)
  return `через ${days} ${plural(days, 'день', 'дня', 'дней')}`
}

const promoLabel = promo => (promo.discount_type === 'fixed'
  ? `−${money(promo.discount_value)} ₽`
  : `−${Number(promo.discount_value)}%`)

/** Строки «скоро закончится» под ценой: про скидку тарифа и про промокод. */
function endingNotes(tariff) {
  const notes = []
  if (tariff.discount_ending_soon) {
    const parts = []
    if (tariff.discount_ends_at) parts.push(`закончится ${timeLeft(tariff.discount_ends_at)}`)
    if (tariff.discount_uses_left != null) parts.push(`осталось ${nPurchases(tariff.discount_uses_left)}`)
    notes.push(`Скидка ${parts.join(', ') || 'скоро закончится'} — успей`)
  }
  const promo = tariff.promo
  if (promo?.ending_soon) {
    const parts = []
    if (promo.expires_at) parts.push(`закончится ${timeLeft(promo.expires_at)}`)
    if (promo.uses_left != null) parts.push(`осталось ${nUses(promo.uses_left)}`)
    notes.push(`Промокод ${promo.code}: ${parts.join(', ') || 'скоро закончится'}`)
  }
  return notes
}

// Группы витрины идут в этом порядке; тариф без своей группы (новый kind из
// админки) не теряется — он попадает в «Другое» в конце списка.
const GROUPS = [
  { kind: 'tarot_subscription', title: 'Подписка на Таро', copy: 'Расклады и вопросы в чате Moonly' },
  { kind: 'astro_package', title: 'Астро-разборы', copy: 'Блоки Формулы души — покупаешь пакет и выбираешь разборы' },
  { kind: 'message_pack', title: 'Дополнительно', copy: 'Докупить вопросы к уже открытому' },
]

/** Короткая подпись срока рядом с ценой: «/ мес» для месячной подписки. */
function periodLabel(tariff) {
  const days = tariff.duration_days
  if (!days) return ''
  if (days === 30) return '/ мес'
  if (days === 365) return '/ год'
  return `/ ${nDays(days)}`
}

/** Что входит в тариф — строки собираются из полей, а не из текста описания. */
function features(tariff) {
  const rows = []
  // `readings_quota` — одна колонка на оба модуля: у астро-пакета она считает разборы,
  // у пробного тарифа расклады. Слово выбирается по scope, иначе пробный обещал бы
  // разбор, которого не даёт. Вместе с «входит всё» квота не дублируется отдельной
  // строкой, а уточняет её: не «все расклады каталога», а «любой один из всего каталога».
  const spreadQuota = tariff.scope === 'tarot' ? tariff.readings_quota : null
  if (tariff.includes_all_spreads) {
    rows.push(spreadQuota ? `${nSpreads(spreadQuota)} на выбор из всего каталога` : 'Все расклады каталога')
  } else if (spreadQuota) {
    rows.push(`${nSpreads(spreadQuota)} на выбор`)
  }
  if (tariff.includes_all_readings) rows.push('Все разборы каталога')
  if (tariff.readings_quota && !spreadQuota) rows.push(`${nReadings(tariff.readings_quota)} на выбор`)
  if (tariff.total_messages) {
    const bonus = tariff.bonus_messages ? `, из них ${tariff.bonus_messages} бонусом` : ''
    rows.push(`${nQuestions(tariff.total_messages)} в чате Moonly${bonus}`)
  }
  if (tariff.duration_days) rows.push(`Доступ на ${nDays(tariff.duration_days)}`)
  // Послание дня сюда не попадает: оно бесплатно у всех и к тарифу отношения не имеет.
  if (tariff.readings_per_day) rows.push(`Не больше ${nReadings(tariff.readings_per_day)} в день`)
  if (tariff.messages_per_day) rows.push(`Не больше ${nQuestions(tariff.messages_per_day)} в день`)
  return rows
}

// Опрос статуса оплаты. Вебхук от кассы обычно доходит за секунды, но карта может
// уйти на 3-D Secure, а человек — задуматься над вводом кода, поэтому ждём долго и
// редко: лишние запросы здесь ничего не ускоряют.
const POLL_INTERVAL_MS = 4000
const POLL_TIMEOUT_MS = 10 * 60 * 1000

export function TariffCard({ tariff, onBuy, onActivate, busy, awaiting, activated }) {
  const [open, setOpen] = useState(false)
  const [months, setMonths] = useState(1)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const discount = Number(tariff.discount_percent) || 0
  const benefit = Number(tariff.compare_at_price) - Number(tariff.price)
  // Пробный тариф стоит 0 ₽: счёт в кассе на такую сумму не выставить, поэтому у него
  // своя кнопка — она сразу зовёт выдачу, без ссылки в браузер и без опроса статуса.
  const isTrial = !!tariff.is_trial
  const monthOption = tariff.month_options?.find((option) => option.months === months)
  const selectedPrice = monthOption?.price ?? tariff.price
  const selectedFullPrice = monthOption?.full_price ?? tariff.full_price
  // Промокод у варианта срока свой: проценты от другой суммы дают другую экономию.
  const promoCode = monthOption ? monthOption.promo_code : tariff.promo?.code
  const promoSaving = Number(monthOption ? monthOption.promo_saving : tariff.promo?.saving) || 0
  const saving = selectedFullPrice ? Number(selectedFullPrice) - Number(selectedPrice) : 0
  const notes = isTrial ? [] : endingNotes(tariff)

  // Состав грузим один раз и только когда его действительно раскрыли.
  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (!next || detail || loading) return
    setLoading(true)
    try { setDetail(await fetchTariff(tariff.id)) } catch { setDetail({ spreads: [], readings: [] }) }
    finally { setLoading(false) }
  }

  const items = [...(detail?.readings || []), ...(detail?.spreads || [])]
  const itemsLabel = detail
    ? [detail.readings?.length ? nReadings(detail.readings.length) : '', detail.spreads?.length ? nSpreads(detail.spreads.length) : '']
      .filter(Boolean).join(' · ')
    : ''

  return (
    <Glass className="tariff-card">
      <div className="tariff-head">
        <Orb />
        <div>
          <b>{tariff.title}</b>
          {tariff.subtitle && <small>{tariff.subtitle}</small>}
        </div>
        {isTrial && <i className="tariff-badge is-gift">Подарок</i>}
        {!isTrial && discount > 0 && <i className="tariff-badge">−{Math.round(discount)}%</i>}
      </div>

      <div className={`tariff-price ${!isTrial && selectedFullPrice ? 'is-discounted' : ''}`}>
        {!isTrial && selectedFullPrice && <s aria-label="Цена без скидки">{money(selectedFullPrice)} ₽</s>}
        <strong>{isTrial ? 'Бесплатно' : `${money(selectedPrice)} ₽`}</strong>
        <span>{months === 1 ? periodLabel(tariff) : `за ${months} мес.`}</span>
      </div>
      {!isTrial && saving > 0 && (
        <p className="tariff-saving">
          Выгода {money(saving)} ₽
          {promoCode && promoSaving > 0 && (
            <i className="tariff-promo"><Ticket size={12} /> {promoCode} −{money(promoSaving)} ₽</i>
          )}
        </p>
      )}
      {notes.map(note => <p key={note} className="tariff-ending"><Clock size={13} /> {note}</p>)}
      {months === 1 && tariff.compare_at_price && benefit > 0 && (
        <p className="tariff-benefit">По отдельности {money(tariff.compare_at_price)} ₽ — выгода {money(benefit)} ₽</p>
      )}

      {tariff.description && <p className="tariff-copy">{tariff.description}</p>}

      <ul className="tariff-features">
        {features(tariff).map(row => <li key={row}>{row}</li>)}
      </ul>

      {tariff.month_options?.length > 1 && (
        <div className="tariff-months">
          <label htmlFor={`months-${tariff.id}`}>Срок подписки</label>
          <select id={`months-${tariff.id}`} value={months} onChange={(event) => setMonths(Number(event.target.value))} disabled={busy || awaiting}>
            {tariff.month_options.map((option) => (
              <option key={option.months} value={option.months}>
                {option.months} {plural(option.months, 'месяц', 'месяца', 'месяцев')} · {money(option.price)} ₽{Number(option.month_discount_percent) > 0 ? ` · −${Number(option.month_discount_percent)}%` : ''}
              </option>
            ))}
          </select>
          <small>Доступ на {nDays(monthOption?.duration_days ?? tariff.duration_days)}. Вопросы и лимиты начисляются за весь срок.</small>
        </div>
      )}

      {isTrial ? (
        <button
          type="button"
          className="tariff-buy press"
          onClick={() => onActivate(tariff)}
          disabled={busy || activated}
        >
          {busy && 'Активируем…'}
          {!busy && activated && <>Уже активирован <Check size={15} /></>}
          {!busy && !activated && <>Забрать бесплатно <Gift size={15} /></>}
        </button>
      ) : (
        <button
          type="button"
          className="tariff-buy press"
          onClick={() => onBuy(tariff, months)}
          disabled={busy || awaiting}
        >
          {busy && 'Готовим оплату…'}
          {!busy && awaiting && 'Ждём подтверждения…'}
          {!busy && !awaiting && <>Оплатить {money(selectedPrice)} ₽ <ExternalLink size={15} /></>}
        </button>
      )}
      {awaiting && (
        <p className="tariff-await">
          Форма оплаты открылась в браузере. Оплати и возвращайся сюда — доступ откроется сам.
        </p>
      )}

      <button type="button" className={`tariff-more press ${open ? 'is-open' : ''}`} onClick={toggle} aria-expanded={open}>
        Что входит {itemsLabel && <em>{itemsLabel}</em>} <ChevronDown size={16} />
      </button>
      {open && (
        <div className="tariff-contents">
          {loading && <p className="result-placeholder">✨ Загружаем состав…</p>}
          {!loading && !items.length && <p className="result-placeholder">Состав уточняется.</p>}
          {items.map(item => (
            <span key={`${item.section || item.sphere}-${item.id}`}>
              <b>{item.title}</b>
              <small>{item.section || item.sphere}</small>
            </span>
          ))}
        </div>
      )}
    </Glass>
  )
}

// Оплата тарифа: выставить счёт, открыть форму кассы и дождаться вебхука опросом.
// Живёт в хуке, а не в экране: той же дорогой платят и из окна «нужна покупка»
// (Paywall в main.jsx), где подходящий тариф предлагается прямо на месте.
export function usePurchase(onAccessChanged) {
  const [error, setError] = useState('')
  // Счёт, который сейчас оплачивают в браузере: {paymentId, tariffId, title}.
  const [pending, setPending] = useState(null)
  const [paid, setPaid] = useState('')
  const [busyId, setBusyId] = useState(null)
  // Текст, которым бэк отвечает вместо ссылки на оплату, когда касса отключена.
  const [notice, setNotice] = useState('')
  // Колбэк обновления доступа держим в ref: иначе новая функция от родителя на
  // каждый рендер перезапускала бы опрос и он бы никогда не досчитал свой таймаут.
  const accessChanged = useRef(onAccessChanged)
  accessChanged.current = onAccessChanged

  // Выставить счёт и уйти платить. Мини-апп при этом НЕ закрывается: оплата идёт в
  // соседнем окне, а мы остаёмся ждать вебхук.
  //
  // Ссылка открывается уже после ответа бэка, то есть вне обработчика тапа. Внутри
  // Telegram это ничего не меняет (openLink не считается всплывающим окном), а вот
  // запасной window.open в обычном браузере может поймать блокировщик — там ссылку
  // придётся открыть вручную. Целевая среда у нас Telegram, так что живём с этим.
  const buy = async (tariff, months = 1) => {
    setBusyId(tariff.id)
    setError('')
    setPaid('')
    try {
      const order = await createPayment(tariff.id, months)
      // Касса отключена: вместо ссылки бэк присылает {message} — показываем его в окне.
      if (!order?.url) {
        setNotice(order?.message || 'Оплата временно недоступна.')
        return
      }
      openExternalLink(order.url)
      setPending({ paymentId: order.payment_id, tariffId: tariff.id, title: tariff.title, months })
    } catch (err) {
      setError(err?.status === 503
        ? 'Оплата временно недоступна — касса не отвечает. Попробуй чуть позже.'
        : 'Не удалось создать счёт. Попробуй ещё раз.')
    } finally {
      setBusyId(null)
    }
  }

  // Ждём подтверждения: оплату подтверждает вебхук кассы, а не возврат в приложение,
  // поэтому узнать результат можно только спросив бэк.
  useEffect(() => {
    if (!pending) return undefined
    let live = true
    const until = Date.now() + POLL_TIMEOUT_MS

    const check = async () => {
      if (!live) return
      try {
        const status = await getPaymentStatus(pending.paymentId)
        if (!live) return
        if (status?.is_paid) {
          setPending(null)
          setPaid(`Оплата прошла. Доступ по тарифу «${status.tariff_title || pending.title}» на ${status.months || pending.months || 1} мес. уже открыт.`)
          hapticNotification('success')
          // Замки в каталоге и счётчик вопросов живут на уровне приложения — просим
          // его перечитать доступ, иначе они разъедутся с реальностью до перезахода.
          accessChanged.current?.()
          return
        }
      } catch {
        // Сеть моргнула или бэк ответил ошибкой — не страшно, есть следующий тик.
      }
      // Ждать бесконечно незачем: человек мог просто закрыть форму, не заплатив.
      if (live && Date.now() > until) setPending(null)
    }

    const timer = setInterval(check, POLL_INTERVAL_MS)
    // Возврат из браузера — самый вероятный момент, когда деньги уже прошли.
    // Спрашиваем сразу, чтобы не показывать «ждём» лишние секунды.
    const onVisible = () => { if (!document.hidden) check() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [pending])

  return { buy, busyId, setBusyId, pending, paid, setPaid, error, setError, notice, setNotice }
}

// Окно с ответом бэка вместо оплаты (usePurchase().notice).
export function PaymentNotice({ text, onClose }) {
  if (!text) return null
  return (
    <div className="ai-modal-backdrop" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <section className="ai-modal" role="alertdialog" aria-modal="true" aria-label="Оплата">
        <button className="ai-modal-close press" onClick={onClose} aria-label="Закрыть"><X /></button>
        <span className="ai-modal-kicker">Оплата</span>
        <h3>Оплата недоступна</h3>
        <div className="ai-modal-copy"><p>{text}</p></div>
      </section>
    </div>
  )
}

// Какой тариф предложить в окне «нужна покупка». Ищем по коду отказа (402) и,
// если известно, по конкретному разбору/раскладу: среди платных позиций нужного
// вида берём те, что его открывают, и из них самую дешёвую. Состав тарифа без
// флага «входит всё» приходит только из GET /tariffs/{id}, но позиций на витрине
// единицы, так что запросы идут параллельно и быстро.
const PAYWALL_KIND = {
  tarot_subscription_required: 'tarot_subscription',
  tarot_spreads_exhausted: 'tarot_subscription',
  astro_package_required: 'astro_package',
  readings_quota_exhausted: 'astro_package',
  ai_messages_exhausted: 'message_pack',
}
export async function suggestTariff(code, item = null) {
  const kind = PAYWALL_KIND[code]
  if (!kind) return null
  const rows = ((await fetchTariffs(kind)) || []).filter(t => !t.is_trial && t.kind === kind)
  const isAstro = kind === 'astro_package'
  const opens = async (tariff) => {
    if (!item?.id || kind === 'message_pack') return true
    if (isAstro ? tariff.includes_all_readings : tariff.includes_all_spreads) return true
    try {
      const detail = await fetchTariff(tariff.id)
      return (isAstro ? detail.readings : detail.spreads)?.some(row => row.id === item.id)
    } catch { return false }
  }
  const fits = await Promise.all(rows.map(opens))
  const matching = rows.filter((_, i) => fits[i])
  return matching.sort((a, b) => Number(a.price) - Number(b.price))[0] || null
}

// Блок «Есть промокод?»: поле ввода и список уже активированных кодов. Скидку он не
// считает — после активации экран перечитывает тарифы, и цены приходят уже с кодом.
const PROMO_STATUS = {
  expired: 'истёк',
  used_up: 'использован',
  exhausted: 'закончился',
  inactive: 'не действует',
  not_started: 'ещё не начал действовать',
}

function PromoBox({ onActivated }) {
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)

  const load = () => fetchMyPromoCodes().then(rows => setCodes(Array.isArray(rows) ? rows : [])).catch(() => {})
  useEffect(() => { load() }, [])

  const submit = async event => {
    event.preventDefault()
    if (!code.trim() || busy) return
    setBusy(true)
    setMessage(null)
    try {
      const promo = await activatePromoCode(code.trim())
      hapticNotification('success')
      setMessage({ ok: true, text: promo.status === 'active'
        ? `Промокод ${promo.code} применён: ${promoLabel(promo)}. Цены ниже уже со скидкой.`
        : `Промокод ${promo.code} ${PROMO_STATUS[promo.status] || 'сейчас не действует'}.` })
      setCode('')
      load()
      onActivated?.()
    } catch (err) {
      hapticNotification('error')
      setMessage({ ok: false, text: err?.detail?.message || 'Не удалось применить промокод. Попробуй ещё раз.' })
    } finally {
      setBusy(false)
    }
  }

  const active = codes.filter(item => item.status === 'active')

  return (
    <Glass className="promo-box">
      <form onSubmit={submit}>
        <label htmlFor="promo-code"><Ticket size={15} /> Есть промокод?</label>
        <div className="promo-row">
          <input
            id="promo-code"
            value={code}
            onChange={event => setCode(event.target.value.toUpperCase())}
            placeholder="Введи код"
            maxLength={64}
            autoCapitalize="characters"
            autoComplete="off"
          />
          <button type="submit" className="press" disabled={busy || !code.trim()}>{busy ? '…' : 'Применить'}</button>
        </div>
      </form>
      {message && <p className={`promo-message ${message.ok ? 'is-ok' : 'is-error'}`} role="status">{message.text}</p>}
      {active.length > 0 && (
        <ul className="promo-list">
          {active.map(item => (
            <li key={item.code}>
              <b>{item.code}</b> <span>{promoLabel(item)}</span>
              {!item.applies_to_all_tariffs && <small>на выбранные тарифы</small>}
              {item.expires_at && <small className={item.ending_soon ? 'is-soon' : ''}>до {parseUtc(item.expires_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</small>}
              {item.uses_left != null && <small className={item.ending_soon ? 'is-soon' : ''}>осталось {nUses(item.uses_left)}</small>}
            </li>
          ))}
        </ul>
      )}
    </Glass>
  )
}

export default function TariffsScreen({ go, back, onAccessChanged, trial, onTrialChanged }) {
  const [tariffs, setTariffs] = useState(null)
  const { buy, busyId, setBusyId, pending, paid, setPaid, error, setError, notice, setNotice } = usePurchase(onAccessChanged)
  const accessChanged = useRef(onAccessChanged)
  accessChanged.current = onAccessChanged
  const trialChanged = useRef(onTrialChanged)
  trialChanged.current = onTrialChanged

  // reload растёт после активации промокода — тарифы перечитываются уже с новой ценой.
  const [reload, setReload] = useState(0)
  useEffect(() => {
    let live = true
    fetchTariffs()
      .then(rows => { if (live) setTariffs(Array.isArray(rows) ? rows : []) })
      .catch(() => {
        if (!live) return
        setTariffs([])
        setError('Не удалось загрузить тарифы. Проверь соединение и попробуй ещё раз.')
      })
    return () => { live = false }
  }, [reload])

  // Забрать бесплатный тариф. Ни ссылки в браузер, ни опроса статуса: платить нечего,
  // бэк выдаёт доступ прямо в ответе, поэтому дальше остаётся только перечитать доступ
  // и статус подарка — по нему кнопка станет «Уже активирован», а всплывашка исчезнет.
  const activate = async tariff => {
    setBusyId(tariff.id)
    setError('')
    setPaid('')
    try {
      await activateTrialTariff()
      hapticNotification('success')
      setPaid(`Тариф «${tariff.title}» активирован. Бесплатный расклад уже доступен.`)
      accessChanged.current?.()
      trialChanged.current?.()
    } catch (err) {
      if (err?.status === 409) {
        // Уже забирал (соседняя вкладка, двойной тап): доступ у человека есть,
        // ошибка ему ни о чём не скажет, поэтому просто подтягиваем актуальный статус.
        trialChanged.current?.()
        setPaid('Бесплатный тариф уже активирован.')
      } else {
        setError(err?.status === 404
          ? 'Бесплатный тариф больше не доступен.'
          : 'Не удалось активировать тариф. Попробуй ещё раз.')
      }
    } finally {
      setBusyId(null)
    }
  }

  const known = GROUPS.map(g => g.kind)
  const groups = [
    ...GROUPS.map(group => ({ ...group, items: (tariffs || []).filter(t => t.kind === group.kind) })),
    { kind: 'other', title: 'Другое', copy: '', items: (tariffs || []).filter(t => !known.includes(t.kind)) },
  ].filter(group => group.items.length > 0)

  return (
    <section className="screen tariffs-screen">
      <header className="topbar">
        <button className="press" onClick={() => go(back)}><ArrowLeft size={17} /> Назад</button>
        <span><Orb /> Тарифы</span>
      </header>
      <h2 className="page-title">Тарифы</h2>
      <p className="subtitle">Подписка на расклады, пакеты разборов и вопросы в чате Moonly</p>
      <SparkLine />

      {error && <p className="api-error" role="alert">{error}</p>}
      <PaymentNotice text={notice} onClose={() => setNotice('')} />
      {paid && <p className="tariff-paid" role="status">{paid}</p>}
      {tariffs === null && !error && <p className="result-placeholder">✨ Загружаем тарифы…</p>}
      {tariffs !== null && !groups.length && !error && <p className="result-placeholder">Тарифы пока не настроены.</p>}
      {tariffs !== null && groups.length > 0 && <PromoBox onActivated={() => setReload(n => n + 1)} />}

      {groups.map(group => (
        <div className="tariff-group" key={group.kind}>
          <h3>{group.title}</h3>
          {group.copy && <p className="subtitle">{group.copy}</p>}
          {group.items.map(tariff => (
            <TariffCard
              key={tariff.id}
              tariff={tariff}
              onBuy={buy}
              onActivate={activate}
              busy={busyId === tariff.id}
              awaiting={pending?.tariffId === tariff.id}
              activated={!!tariff.is_trial && !!trial?.activated}
            />
          ))}
        </div>
      ))}

      <Glass className="question-card">
        <div><b>Остались вопросы по тарифам?</b><span>Спроси меня в чате — подскажу, что подойдёт</span></div>
        <Pill onClick={() => go('chat')}>Написать</Pill>
      </Glass>
    </section>
  )
}
